import { Component } from "@theme/component";
import { QuantitySelectorUpdateEvent } from "@theme/events";
import { parseIntOrDefault } from "@theme/utilities";

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ["quantityInput", "minusButton", "plusButton"];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    this.updateCartQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      "data-cart-quantity",
      cartQty.toString(),
    );
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Gets the normalized quantity value based on current rules.
   * @returns {number}
   */
  getNormalizedValue() {
    const { min, step, value } = this.getCurrentValues();
    const rules = this.getStaffelRules();

    if (rules.enabled) {
      return this.normalizeStaffelAddition(value, "nearest");
    }

    if ((value - min) % step !== 0) {
      return min + Math.round((value - min) / step) * step;
    }

    return value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Gets staffel quantity rules from data attributes when available.
   * @returns {{enabled: boolean, orderStep: number, unitsPerBox: number, baseMin: number}}
   */
  getStaffelRules() {
    const { quantityInput } = this.refs;
    const orderStep = parseIntOrDefault(quantityInput.dataset.orderStep, 0);
    const unitsPerBox = parseIntOrDefault(quantityInput.dataset.unitsPerBox, 0);
    const baseMin = parseIntOrDefault(quantityInput.dataset.baseMin, 1);

    return {
      enabled: orderStep > 0 && unitsPerBox > 0,
      orderStep,
      unitsPerBox,
      baseMin,
    };
  }

  /**
   * Gets the effective minimum add quantity for this selector.
   * Product page semantics are "how many to add", so this depends on cart state.
   * @returns {number}
   */
  getEffectiveMin() {
    const { min } = this.getCurrentValues();
    const rules = this.getStaffelRules();

    if (!rules.enabled) return min;

    return this.normalizeStaffelValue(1, "up") ?? min;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step, staffelRules = {}) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    if (typeof staffelRules.orderStep === "string") {
      quantityInput.dataset.orderStep = staffelRules.orderStep;
    } else {
      delete quantityInput.dataset.orderStep;
    }

    if (typeof staffelRules.unitsPerBox === "string") {
      quantityInput.dataset.unitsPerBox = staffelRules.unitsPerBox;
    } else {
      delete quantityInput.dataset.unitsPerBox;
    }

    if (typeof staffelRules.baseMin === "string") {
      quantityInput.dataset.baseMin = staffelRules.baseMin;
    }

    const rules = this.getStaffelRules();
    const fallbackMin = rules.enabled
      ? Math.max(parseIntOrDefault(min, 1), rules.orderStep)
      : parseIntOrDefault(min, 1);
    const effectiveMin = rules.enabled ? this.getEffectiveMin() : fallbackMin;

    quantityInput.min = effectiveMin.toString();
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute("max");
    }
    quantityInput.step = rules.enabled ? rules.orderStep.toString() : step;

    const effectiveMax = this.getEffectiveMax();
    let newValue = currentValue;

    if (rules.enabled) {
      newValue =
        this.normalizeStaffelValue(currentValue, "down") ?? effectiveMin;
    } else {
      const newMin = parseIntOrDefault(effectiveMin, 1);
      const newStep = parseIntOrDefault(step, 1);
      if ((currentValue - newMin) % newStep !== 0) {
        newValue =
          newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
      }
    }

    // Ensure value is within bounds
    newValue = Math.max(
      parseIntOrDefault(effectiveMin, 1),
      Math.min(effectiveMax ?? Infinity, newValue),
    );

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(
        quantityInput.getAttribute("data-cart-quantity"),
        0,
      ),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;

    const remainingQuantity = max - cartQuantity;
    const rules = this.getStaffelRules();

    if (!rules.enabled) {
      return Math.max(remainingQuantity, min);
    }

    if (remainingQuantity <= 0) {
      return 0;
    }

    return this.normalizeStaffelAddition(remainingQuantity, "down") ?? 0;
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMin = this.getEffectiveMin();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= effectiveMin;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { step, value } = this.getCurrentValues();
    const effectiveMin = this.getEffectiveMin();
    const effectiveMax = this.getEffectiveMax();
    const rules = this.getStaffelRules();
    let newValue;

    if (rules.enabled) {
      newValue =
        this.normalizeStaffelValue(
          value + (stepMultiplier > 0 ? 1 : -1),
          stepMultiplier > 0 ? "up" : "down",
        ) ?? value;
    } else {
      newValue = value + step * stepMultiplier;
    }

    newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(effectiveMin, newValue),
    );

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (quantityInput.readOnly) return;

    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    )
      return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.readOnly) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { step } = this.getCurrentValues();
    const effectiveMin = this.getEffectiveMin();
    const effectiveMax = this.getEffectiveMax();
    const rules = this.getStaffelRules();

    let quantity = Math.min(
      effectiveMax ?? Infinity,
      Math.max(effectiveMin, parseInt(event.target.value) || 0),
    );

    if (rules.enabled) {
      quantity =
        this.normalizeStaffelValue(quantity, "nearest") ?? effectiveMin;
    } else if ((quantity - effectiveMin) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine) || undefined,
      ),
    );
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMin = this.getEffectiveMin();
    const effectiveMax = this.getEffectiveMax();
    const rules = this.getStaffelRules();
    let nextValue = value;

    quantityInput.min = effectiveMin.toString();

    if (rules.enabled) {
      nextValue =
        this.normalizeStaffelValue(value, "down") ??
        this.normalizeStaffelValue(1, "up") ??
        effectiveMin;
    }

    const clampedValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(effectiveMin, nextValue),
    );

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
      this.onQuantityChange();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />',
      );
    }

    return this.refs.quantityInput;
  }

  /**
   * Normalizes add quantities against the current cart quantity.
   * Below one box, totals use order-step increments; from one box onward, totals use full boxes.
   * @param {number} quantity
   * @param {"nearest" | "down" | "up"} mode
   * @returns {number | null}
   */
  normalizeStaffelAddition(quantity, mode = "nearest") {
    const rules = this.getStaffelRules();
    if (!rules.enabled) return quantity;

    const { cartQuantity } = this.getCurrentValues();
    const targetTotal = cartQuantity + Math.max(1, quantity);
    const normalizedTotal = this.normalizeStaffelTotalQuantity(
      targetTotal,
      mode,
      { minimumTotal: cartQuantity + 1 },
    );

    return normalizedTotal === null ? null : normalizedTotal - cartQuantity;
  }

  /**
   * Product-page selectors normalize add quantities.
   * Subclasses with absolute quantity semantics can override this.
   * @param {number} quantity
   * @param {"nearest" | "down" | "up"} mode
   * @returns {number | null}
   */
  normalizeStaffelValue(quantity, mode = "nearest") {
    return this.normalizeStaffelAddition(quantity, mode);
  }

  /**
   * Normalizes an absolute total quantity against staffel rules.
   * @param {number} quantity
   * @param {"nearest" | "down" | "up"} mode
   * @param {{ minimumTotal?: number }} [options]
   * @returns {number | null}
   */
  normalizeStaffelTotalQuantity(quantity, mode = "nearest", options = {}) {
    const rules = this.getStaffelRules();
    if (!rules.enabled) return quantity;

    const { min } = this.getCurrentValues();
    const minimumBelowBox = Math.max(rules.baseMin, rules.orderStep);
    const allowZero = min === 0;

    if (allowZero && quantity <= 0) {
      return 0;
    }

    const minimumTotal = options.minimumTotal ?? minimumBelowBox;
    const targetTotal = Math.max(minimumTotal, quantity);
    const candidates = new Set();

    if (minimumBelowBox < rules.unitsPerBox) {
      const relativeTarget = targetTotal - minimumBelowBox;
      const lowerBelow =
        minimumBelowBox +
        Math.floor(relativeTarget / rules.orderStep) * rules.orderStep;
      const upperBelow =
        minimumBelowBox +
        Math.ceil(relativeTarget / rules.orderStep) * rules.orderStep;

      [lowerBelow, upperBelow].forEach((candidate) => {
        if (
          Number.isFinite(candidate) &&
          candidate >= minimumBelowBox &&
          candidate <= rules.unitsPerBox
        ) {
          candidates.add(candidate);
        }
      });
    }

    const lowerBox =
      Math.floor(targetTotal / rules.unitsPerBox) * rules.unitsPerBox;
    const upperBox =
      Math.ceil(targetTotal / rules.unitsPerBox) * rules.unitsPerBox;

    [rules.unitsPerBox, lowerBox, upperBox].forEach((candidate) => {
      if (Number.isFinite(candidate) && candidate >= rules.unitsPerBox) {
        candidates.add(candidate);
      }
    });

    const validTotals = Array.from(candidates)
      .filter((candidate) => candidate >= minimumTotal)
      .sort((left, right) => left - right);

    if (validTotals.length === 0) {
      return null;
    }

    if (mode === "up") {
      const nextTotal =
        validTotals.find((candidate) => candidate >= targetTotal) ??
        validTotals[validTotals.length - 1];
      return nextTotal ?? null;
    }

    if (mode === "down") {
      const previousTotal = [...validTotals]
        .reverse()
        .find((candidate) => candidate <= targetTotal);
      return previousTotal ?? null;
    }

    const nearestTotal = validTotals.reduce((bestCandidate, candidate) => {
      if (bestCandidate === null) return candidate;

      const bestDistance = Math.abs(bestCandidate - targetTotal);
      const candidateDistance = Math.abs(candidate - targetTotal);

      if (candidateDistance < bestDistance) return candidate;
      if (candidateDistance === bestDistance && candidate < bestCandidate) {
        return candidate;
      }

      return bestCandidate;
    }, null);

    return nearestTotal ?? null;
  }
}

if (!customElements.get("quantity-selector-component")) {
  customElements.define(
    "quantity-selector-component",
    QuantitySelectorComponent,
  );
}
